import { ApiTokensResource } from '../resources/api-tokens.js';
import { BankAccountsResource } from '../resources/bank-accounts.js';
import { BanksResource } from '../resources/banks.js';
import { SimulatorResource } from '../resources/simulator.js';
import { TransactionsResource } from '../resources/transactions.js';
import { WebhookHistoriesResource } from '../resources/webhook-histories.js';
import { WebhookSettingsResource } from '../resources/webhook-settings.js';
import type { RequestOptions } from '../types/common.js';
import type { ApiTokenScope } from '../types/enums.js';
import { resolveConfig, type GpmPayOptions } from './config.js';
import { GpmPayPermissionError } from './errors.js';
import { HttpClient } from './http.js';
import type { HttpMethod } from './retry.js';
import { maskToken, tokenPrefix } from './token.js';

export interface PingResult {
  ok: true;
  baseUrl: string;
  /** The token's public prefix — safe to print. */
  tokenPrefix: string;
  latencyMs: number;
  /**
   * Which scopes the token actually carries, determined by probing one cheap
   * read per scope. A scope lands in `denied` only on a genuine 403; any other
   * failure (network, 5xx) propagates instead of being reported as missing.
   */
  scopes: { granted: ApiTokenScope[]; denied: ApiTokenScope[] };
}

/** One cheap read per scope, used by {@link GpmPay.ping} to detect grants. */
const SCOPE_PROBES: ReadonlyArray<{ scope: ApiTokenScope; path: string }> = [
  { scope: 'transactions:read', path: '/transactions' },
  { scope: 'bank-accounts:read', path: '/bank-accounts' },
  { scope: 'webhooks:manage', path: '/webhook-settings' },
];

/**
 * GPM Pay API client.
 *
 * An API token is **required** — the constructor throws immediately if one is
 * missing or malformed, rather than deferring to a 401 on the first call.
 *
 * @example
 * import { GpmPay } from '@gpmpay/sdk';
 *
 * const client = new GpmPay({ apiToken: process.env.GPMPAY_API_TOKEN! });
 * const page = await client.transactions.list({ limit: 10 });
 * console.log(page.data[0]?.transferContent);
 */
export class GpmPay {
  readonly transactions: TransactionsResource;
  readonly bankAccounts: BankAccountsResource;
  readonly banks: BanksResource;
  readonly webhookSettings: WebhookSettingsResource;
  readonly webhookHistories: WebhookHistoriesResource;
  readonly apiTokens: ApiTokensResource;
  readonly simulator: SimulatorResource;

  private readonly http: HttpClient;
  private readonly token: string;

  /** @throws {GpmPayConfigError} when `apiToken` is missing or malformed. */
  constructor(options: GpmPayOptions) {
    const config = resolveConfig(options);
    this.token = config.apiToken;
    this.http = new HttpClient(config);

    this.transactions = new TransactionsResource(this.http);
    this.bankAccounts = new BankAccountsResource(this.http);
    this.banks = new BanksResource(this.http);
    this.webhookSettings = new WebhookSettingsResource(this.http);
    this.webhookHistories = new WebhookHistoriesResource(this.http);
    this.apiTokens = new ApiTokensResource(this.http);
    this.simulator = new SimulatorResource(this.http);
  }

  /**
   * Build a client from `GPMPAY_API_TOKEN` and `GPMPAY_API_URL`.
   *
   * @throws {GpmPayConfigError} when `GPMPAY_API_TOKEN` is not set.
   */
  static fromEnv(overrides: Partial<GpmPayOptions> = {}): GpmPay {
    const envToken = process.env.GPMPAY_API_TOKEN;
    const envUrl = process.env.GPMPAY_API_URL;

    const options: GpmPayOptions = {
      ...overrides,
      apiToken: overrides.apiToken ?? (envToken as string),
    };
    const baseUrl = overrides.baseUrl ?? envUrl;
    if (baseUrl !== undefined) options.baseUrl = baseUrl;

    return new GpmPay(options);
  }

  /** Fully normalized API base, e.g. `https://api.gpmpay.com/api/v1`. */
  get baseUrl(): string {
    return this.http.baseUrl;
  }

  /** First 12 chars of the token. Safe to log and quote in support tickets. */
  get tokenPrefix(): string {
    return tokenPrefix(this.token);
  }

  toString(): string {
    return `GpmPay(${this.baseUrl}, ${maskToken(this.token)})`;
  }

  /** Ensures `console.log(client)` can never dump the token. */
  [Symbol.for('nodejs.util.inspect.custom')](): string {
    return this.toString();
  }

  /**
   * Verify connectivity and authentication, and report the token's real scopes.
   *
   * Issues one minimal read per scope in parallel and infers the grant from the
   * outcome. The token's own record cannot be used for this: `GET /api-tokens`
   * declares no `@ApiScopes()`, so the fail-closed guard rejects every API
   * token that asks for it.
   *
   * A 401 from the first probe to complete propagates — an invalid token is a
   * hard failure, not "no scopes".
   */
  async ping(options: RequestOptions = {}): Promise<PingResult> {
    const startedAt = Date.now();

    const results = await Promise.all(
      SCOPE_PROBES.map(async ({ scope, path }) => {
        try {
          await this.http.requestList<unknown>('GET', path, {
            ...options,
            query: { limit: 1 },
          });
          return { scope, granted: true };
        } catch (error) {
          if (error instanceof GpmPayPermissionError) {
            return { scope, granted: false };
          }
          throw error;
        }
      }),
    );

    const latencyMs = Date.now() - startedAt;

    return {
      ok: true,
      baseUrl: this.baseUrl,
      tokenPrefix: this.tokenPrefix,
      latencyMs,
      scopes: {
        granted: results.filter((r) => r.granted).map((r) => r.scope),
        denied: results.filter((r) => !r.granted).map((r) => r.scope),
      },
    };
  }

  /**
   * Escape hatch for endpoints this SDK does not model yet.
   *
   * @example
   * await client.request('GET', '/some/new/endpoint');
   */
  request<T = unknown>(
    method: HttpMethod,
    path: string,
    options: RequestOptions & { query?: Record<string, unknown>; body?: unknown } = {},
  ): Promise<T> {
    return this.http.request<T>(method, path, options);
  }
}
